import React from "react";
import { Modal } from "@shopify/polaris";
import styles from "./DeleteModal.module.css";
import MainFrameContextType from "../../types/MainFrameContextType";
import { MainFrameContext } from "../contexts/MainFrameContext";
import { ClipLoader } from "react-spinners";
import { useDispatch, useSelector } from "react-redux";
import { deleteAssetsBulkAction } from "../../store/actions/Asset";

import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import { ImageDeleteData } from "../../types/ImageDeleteData";
import { AssetType } from "../../types/AssetType";
import { PortalState } from "../../types/PortalState";

interface Props {
    active: boolean;
    closeModal: () => void;
}

export default function DeleteBulkModal(props: Props) {
    const { createToast } = React.useContext(
        MainFrameContext
    ) as MainFrameContextType;
    const [error, setError] = React.useState<null | string>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    const imagesDeleteList: ImageDeleteData[] = useSelector(
        (state: any) => state.portal.activeInspection.ImageDeleteList
    );
    const currentInspection = useSelector(
        (state: PortalState) => state.portal.activeInspection
    );

    async function deleteElement() {
        setLoading(true);
        try {
            if (currentInspection?.Id) {
                await dispatch(
                    deleteAssetsBulkAction(
                        currentInspection.Id,
                        imagesDeleteList,
                        AssetType.Image
                    )
                );
                createToast("success", t("inspection.bulkDeleteModal.success"));
                ReactGA.event({
                    category: "Bulk delete element",
                    action: "success",
                    label: `${imagesDeleteList.length}`,
                    value: 0
                });
                setLoading(false);
                props.closeModal();
            }
        } catch (e) {
            console.error(e);
            setError(t("inspection.bulkDeleteModal.failure"));
            ReactGA.event({
                category: "Delete element",
                action: "failed",
                label: `${imagesDeleteList.length}`,
                value: 0
            });
            setLoading(false);
        }
    }

    React.useEffect(() => {
        setError(null);
    }, [props.active]);

    const deleteCount = imagesDeleteList ? imagesDeleteList.length : 0;

    return (
        <Modal
            open={props.active}
            onClose={props.closeModal}
            title={t("inspection.bulkDeleteModal.title")}
        >
            <Modal.Section>
                <div>
                    <div className={styles.modalIcon}>
                        <img
                            width="105px"
                            height="129px"
                            src="/images/Large-Trash.svg"
                            alt="Bin"
                        />
                    </div>
                    <div>
                        <p className={styles.modalText}>
                            {t("inspection.bulkDeleteModal.question")}{" "}
                            {deleteCount}{" "}
                            {t("inspection.bulkDeleteModal.questionEnd")}
                        </p>
                    </div>
                    <div className={styles.modalButtons}>
                        <div
                            onClick={() => {
                                props.closeModal();
                            }}
                            className={styles.cancelButton}
                        >
                            {t("inspection.bulkDeleteModal.cancel")}
                        </div>
                        <div
                            onClick={deleteElement}
                            className={styles.deleteButton}
                        >
                            {loading ? (
                                <div className={styles.modalLoader}>
                                    <ClipLoader
                                        color={"#FFFFFF"}
                                        loading={true}
                                        size={"30px"}
                                    />
                                </div>
                            ) : (
                                <div>
                                    {t("inspection.bulkDeleteModal.delete")}
                                </div>
                            )}
                        </div>
                    </div>
                    {error ? <div className={styles.error}>{error}</div> : null}
                </div>
            </Modal.Section>
        </Modal>
    );
}
